import { API_ENDPOINTS } from '../config/apiConfig';

/** When a series opens and closes, as scheduled in the backend. */
export interface SeriesWindow {
  openingDate: string;
  closingDate: string;
}

/**
 * Series windows for the signed-in student's grade, keyed by series name.
 *
 * The dashboard previously took these from a hard-coded file, so rescheduling a
 * series in the LMS had no effect on what students saw: a series shown as closed
 * could be open, and the countdowns belonged to whatever the file happened to say.
 *
 * Loaded once per grade and cached, since every card asks for the same answer.
 */
const cache = new Map<string, Record<string, SeriesWindow>>();

let inFlight: Promise<Record<string, SeriesWindow>> | null = null;

export const getCachedSeriesWindows = (grade: string): Record<string, SeriesWindow> | null =>
  cache.get(grade) ?? null;

export const loadSeriesWindows = async (grade: string): Promise<Record<string, SeriesWindow>> => {
  const cached = cache.get(grade);
  if (cached) {
    return cached;
  }
  if (inFlight) {
    return inFlight;
  }

  inFlight = (async () => {
    const windows: Record<string, SeriesWindow> = {};
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_ENDPOINTS.learningApi}/packages?grade=${encodeURIComponent(grade)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const body = await response.json();
      const packages: any[] = body?.data?.packages ?? [];

      packages.forEach((pack) => {
        const series = pack?.series?.[0];
        if (!series || !pack.openingDate || !pack.closingDate) {
          return;
        }
        // Every test in a series shares its window; if they ever disagree, take the
        // widest span so a student is never told a test is shut while it is open.
        const existing = windows[series];
        windows[series] = existing
          ? {
              openingDate: pack.openingDate < existing.openingDate ? pack.openingDate : existing.openingDate,
              closingDate: pack.closingDate > existing.closingDate ? pack.closingDate : existing.closingDate,
            }
          : { openingDate: pack.openingDate, closingDate: pack.closingDate };
      });

      cache.set(grade, windows);
    } catch {
      // Leave the cache empty so callers fall back to the static config.
    } finally {
      inFlight = null;
    }
    return windows;
  })();

  return inFlight;
};

export default loadSeriesWindows;
